import type { AdminState, TemporarySchedule, ClassSession, DisplayPayload, MediaAsset, PlaylistItem, ScreenSettings, TemplateContent } from '../shared';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (init.method && init.method !== 'GET') headers.set('x-signage-admin', '1');
  const response = await fetch(url, { ...init, headers, credentials: 'same-origin' });
  if (!response.ok) {
    let message = `Request failed (${response.status}).`;
    try {
      const payload = await response.json() as { error?: string };
      if (payload.error) message = payload.error;
    } catch {
      // Keep the status-based message for non-JSON failures.
    }
    throw new ApiError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  authStatus: () => request<{ authenticated: boolean }>('/api/auth/status'),
  login: (password: string) => request<{ authenticated: boolean }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  adminState: () => request<AdminState>('/api/admin/state'),
  uploadMedia: (form: FormData) => request<MediaAsset>('/api/admin/media', { method: 'POST', body: form }),
  updateMedia: (id: string, patch: Pick<MediaAsset, 'title' | 'active' | 'category' | 'schedule'>) => request<MediaAsset>(`/api/admin/media/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteMedia: (id: string) => request<{ deleted: true; removedPlaylistItems: number }>(`/api/admin/media/${id}`, { method: 'DELETE' }),
  createTemplate: (input: { title: string; category: MediaAsset['category']; template: TemplateContent }) => request<MediaAsset>('/api/admin/templates', { method: 'POST', body: JSON.stringify(input) }),
  savePlaylist: (screenId: string, items: PlaylistItem[]) => request<PlaylistItem[]>(`/api/admin/screens/${screenId}/playlist`, {
    method: 'PUT',
    body: JSON.stringify({ items: items.map(({ id, mediaId, durationSeconds, active, fit, schedule }) => ({ id, mediaId, durationSeconds, active, fit, schedule })) })
  }),
  saveScreenSettings: (screenId: string, settings: ScreenSettings) => request<ScreenSettings>(`/api/admin/screens/${screenId}/settings`, {
    method: 'PUT',
    body: JSON.stringify({ mode: settings.mode, targetLoopSeconds: settings.targetLoopSeconds, categoryWeights: settings.categoryWeights, timeRules: settings.timeRules })
  }),
  saveTemporarySchedules: (schedules: TemporarySchedule[]) => request<TemporarySchedule[]>('/api/admin/temporary-schedules', { method: 'PUT', body: JSON.stringify({ schedules }) }),
  saveClassSessions: (sessions: ClassSession[]) => request<ClassSession[]>('/api/admin/class-sessions', { method: 'PUT', body: JSON.stringify({ sessions }) }),
  display: (slug: string, signal?: AbortSignal) => request<DisplayPayload>(`/api/display/${encodeURIComponent(slug)}`, { signal, cache: 'no-store' })
};
